/**
 * PAP-via-metafield — admin-only "Mark as PAP" action on a product-sync row.
 *
 * Confirms, then calls productSync.markProductAsPap: the backend writes
 * `custom.fulfillment_type='pap'` on every variant + the product-level `pap`
 * tag (tag last), then adds the product to the ERP. The tag is what flips
 * classification (the product leaves this POD onboarding list); a refused
 * import leaves it waiting in the PAP view. A consequential, confirmed action.
 * Self-contained (own dialog + mutation + query invalidation) so
 * ProductSyncRow stays dumb.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, PackageCheck } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PreviewProduct } from '../lib/types';

export function MarkAsPapButton({ product }: { product: PreviewProduct }) {
  const { t } = useTranslation('productSync');
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();

  const markAsPap = trpc.productSync.markProductAsPap.useMutation({
    onSuccess: (result) => {
      setOpen(false);
      // The backend adds the product to the ERP once the tag stands. A refused
      // import keeps the tag, so the product leaves this list either way; say
      // where it went so it doesn't look like it vanished into a gap.
      const name = result.productTitle;
      const { status, draftInventoryHeld } = result.erpImport;
      if (status === 'waiting') {
        toast.warning(t('markAsPap.successWaiting', { name }));
      } else if (status === 'partially_imported') {
        toast.warning(t('markAsPap.successPartial', { name }));
      } else if (draftInventoryHeld) {
        toast.success(t('markAsPap.successDraft', { name }));
      } else {
        toast.success(t('markAsPap.success', { name }));
      }
      // Phase 88: the tag stands, but the product's store SKU set-up rows
      // could not be removed. The backend reports it to Sentry; the operator
      // sees translated copy (never the raw backend sentence) that says so,
      // and it stays until dismissed.
      if (result.warning) toast.warning(t('markAsPap.declaredCleanupWarning', { name }), { duration: Infinity });
      void utils.productSync.getProductSyncPreview.invalidate();
      void utils.productSync.getProductSyncCoverage.invalidate();
    },
    onError: (e) => {
      // Backend messages are operator-facing (B3 already-POD-applied guard,
      // ACCESS_DENIED scope hint, partial tag failure + retry note).
      toast.error(t('markAsPap.error', { message: e.message }));
    },
  });

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="gap-1.5"
        onClick={() => setOpen(true)}
      >
        <PackageCheck className="h-3.5 w-3.5" aria-hidden="true" />
        {t('markAsPap.action')}
      </Button>

      <Dialog open={open} onOpenChange={(next) => !markAsPap.isPending && setOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('markAsPap.dialogTitle', { name: product.title })}</DialogTitle>
            <DialogDescription>{t('markAsPap.dialogBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={markAsPap.isPending}>
              {t('markAsPap.cancel')}
            </Button>
            <Button
              onClick={() => markAsPap.mutate({ productId: product.id })}
              disabled={markAsPap.isPending}
              className="gap-1.5"
            >
              {markAsPap.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {t('markAsPap.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
